import Axios, { AxiosError } from 'axios';
import { useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';

import { HEADER_ID } from '@/components/templates/PageLayout';
import { ReactSingleSelectInput, SelectForm } from '@/components/uiParts';
import {
  getFilePath,
  getRequestImageUrl,
  PresignedUrl,
  reRequestMessage,
  validateMessage,
} from '@/domain/models/Image';
import { OrderMethod } from '@/domain/models/Order/orderMethod';
import { TaxRate } from '@/domain/models/Order/pricePerTax';
import { DateDomainService } from '@/domain/services';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nullToEmptyString } from '@/lib/util';
import {
  IMAGE_RULE,
  validateImageCapacity,
} from '@/pages/camel-order/sites/service';
import { BUTTON_AREA_ID } from '@/pages/menu-managements/components/TopButtonArea';
import {
  BrandItemDeliveryForm,
  BrandItemEatInForm,
  BrandItemTakeoutForm,
  MANAGEMENTITEMLISTACTION_TYPE,
  ManagementItemlistActionType,
  validateUploadImageSize,
} from '@/pages/menu-managements/items/service';
import {
  ItemFormError,
  validateDeliveryPrice,
  validateDescription,
  validateEatInPrice,
  validateMemo,
  validateRelativeSalesPeriod,
  validateSalesPeriod,
  validateSpecialInstructionsPlaceholder,
  validateSpecialInstructionsTitle,
  validateTakeoutPrice,
  validateTitleJa,
} from '@/pages/menu-managements/items/validate';
import {
  getSearchItems,
  SearchItem,
} from '@/pages/menu-managements/services/searchItem';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import {
  BrandItemDelivery,
  BrandItemEatIn,
  BrandItemTakeout,
  SpecialInstructionsSetting,
} from '@/services/BrandItem';
import { useMenuManagementCreateBrandItem } from '@/services/BrandItem/hooks/create/useMenuManagementCreateBrandItem';
import { useMenuManagementDeleteBrandItem } from '@/services/BrandItem/hooks/delete/useMenuManagementDeleteBrandItem';
import { useMenuManagementDuplicateBrandItem } from '@/services/BrandItem/hooks/duplicate/useMenuManagementDuplicateBrandItem';
import { useMenuManagementBrandItemShow } from '@/services/BrandItem/hooks/show/useMenuManagementBrandItemShow';
import { useMenuManagementUpdateBrandItem } from '@/services/BrandItem/hooks/update/useMenuManagementUpdateBrandItem';
import { useMenuManagementSearchBrandOptionGroups } from '@/services/BrandOptionGroup/hooks/search/useMenuManagementSearchBrandOptionGroups';
import { useCamelOrderPresigned } from '@/services/CamelOrder/hooks/presignedUrl/usePresigned';
import { validMaxNum, validMinNum } from '@/validate';
export type CanServeAlone = '0' | '1';
export type IsAuthRequired = '0' | '1';
export type Page = 'new' | 'edit';
export type SalesPeriodType = 'NONE' | 'ABSOLUTE' | 'RELATIVE';
export const SalesPeriodTypes = {
  NONE: 'NONE',
  ABSOLUTE: 'ABSOLUTE',
  RELATIVE: 'RELATIVE',
} as const;

export const useMenuManagementItemForm = () => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);
  const [timer, setTimer] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchItems, setSearchItems] = useState<SearchItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SearchItem[]>([]);
  const [searchItemName, setSearchItemName] = useState<string>('');
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [brandSelect, setBrandSelect] = useState<SelectForm | null>({
    label: '',
    value: '',
  });

  const [limitOfPurchase, setLimitOfPurchase] = useState<number | null>(null);
  const [canServeAlone, setCanServeAlone] = useState<CanServeAlone>('1');
  const [isAuthRequired, setIsAuthRequired] = useState<IsAuthRequired>('0');
  const [titleJa, setTitleJa] = useState('');
  const [orderMethods, setOrderMethods] = useState<OrderMethod[]>([]);
  const [image, setImage] = useState<PresignedUrl>({
    image_url: '',
    file: undefined,
    presigned_url: '',
  });
  const [memo, setMemo] = useState('');
  const [description, setDescription] = useState('');
  const [salesStartAt, setSalesStartAt] = useState<Date | null>(null);
  const [salesEndAt, setSalesEndAt] = useState<Date | null>(null);
  const [salesRelativeStartAt, setSalesRelativeStartAt] = useState<number>(0);
  const [salesRelativeEndAt, setSalesRelativeEndAt] = useState<number>(0);
  const [takeout, setTakeout] = useState<BrandItemTakeoutForm>({
    price: '',
    tax_rate: 8,
    pos_code: '',
    pos_code_case_option_group_item: '',
  });
  const [delivery, setDelivery] = useState<BrandItemDeliveryForm>({
    price: '',
    tax_rate: 8,
    pos_code: '',
    pos_code_case_option_group_item: '',
  });
  const [eatIn, setEatIn] = useState<BrandItemEatInForm>({
    price: '',
    tax_rate: 10,
    pos_code: '',
    pos_code_case_option_group_item: '',
  });
  // 商品に関する要望欄の設定
  const [specialInstructionsEnabled, setSpecialInstructionsEnabled] =
    useState<boolean>(false);
  const [specialInstructionsTitle, setSpecialInstructionsTitle] =
    useState<string>('');
  const [specialInstructionsPlaceholder, setSpecialInstructionsPlaceholder] =
    useState<string>('');
  const [specialInstructionsRequired, setSpecialInstructionsRequired] =
    useState<boolean>(false);
  const [salesPeriodType, setSalesPeriodType] =
    useState<SalesPeriodType>('NONE');
  const errorObj = { message: '', isError: false, isTouched: false };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<ItemFormError>({
    titleJa: { ...errorObj },
    memo: { ...errorObj },
    limitOfPurchase: { ...errorObj },
    description: { ...errorObj },
    salesPeriod: { ...errorObj },
    takeoutPrice: { ...errorObj },
    deliveryPrice: { ...errorObj },
    eatInPrice: { ...errorObj },
    relativeSalesPeriod: { ...errorObj },
    specialInstructionsTitle: { ...errorObj },
    specialInstructionsPlaceholder: { ...errorObj },
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIsDragged] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialStringifyFormState, setInitialStringifyFormState] =
    useState<string>('');
  const listUrl = routes.menuManagementItems.path;

  const associateBrandSelectInput: ReactSingleSelectInput = {
    value: brandSelect,
    options: associateBrandSelectOptions,
    onChange: (brand: SelectForm | null) => {
      if (brand?.value === brandSelect?.value) return;
      setBrandSelect(brand);
      setSelectedItems([]);
    },
    onReset: () => {
      setBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const handleChangeCanServeAlone = (value: React.ReactText) => {
    setCanServeAlone(value as CanServeAlone);
  };

  const handleChangeIsAuthRequired = (value: IsAuthRequired) => {
    setIsAuthRequired(value);
  };

  const handleSalesPeriodType = (value: SalesPeriodType) => {
    setSalesPeriodType(value);
  };
  const handleChangeOrderMethod = (e: React.ChangeEvent<HTMLInputElement>) => {
    const copyOrderMethods = [...orderMethods];
    const isChecked = e.target.checked;
    const newOrderMethod = e.target.value as OrderMethod;
    if (isChecked) {
      setOrderMethods([...copyOrderMethods, newOrderMethod]);
    } else {
      setOrderMethods((prev) => {
        return prev.filter((orderMethod) => orderMethod !== newOrderMethod);
      });
    }
  };

  const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitleJa = e.target.value;
    const newError = validateTitleJa(newTitleJa, error);
    setError(newError), setTitleJa(newTitleJa);
  };

  const handleChangeImageUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files === null) return;
    const file = files[0];
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);

      const fr = new FileReader();
      fr.onload = async () => {
        const base64Url = fr.result as string; /* base64の形で取得 */
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const { size, type } = file;
        if (validateUploadImageSize.check(width, height)) {
          return alert(validateUploadImageSize.message);
        }
        const maxSize = IMAGE_RULE.LOGO.maxSize;
        if (validateImageCapacity.check(size, maxSize)) {
          return alert(validateImageCapacity.message(maxSize));
        }
        const now = new Date();
        const filePath = getFilePath(now);
        const { url } = await useCamelOrderPresigned({
          filePath,
          contentType: type,
        });
        setImage({ image_url: base64Url, file: file, presigned_url: url });
      };
      fr.readAsDataURL(file);
    };
    img.src = URL.createObjectURL(file);
  };

  const handleClearImage = () => {
    setImage({ ...image, image_url: '', presigned_url: '' });
  };

  const handleInputMemo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMemo = e.target.value;
    const newError = validateMemo(newMemo, error);
    setError(newError), setMemo(newMemo);
  };

  const handleInputDescription = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newDescription = e.target.value;
    const newError = validateDescription(newDescription, error);
    setError(newError), setDescription(newDescription);
  };

  const handleInputSalesStartAt = (date: Date | null) => {
    const newError = validateSalesPeriod(date, salesEndAt, error);
    setError(newError), setSalesStartAt(date);
  };

  const handleInputSalesEndAt = (date: Date | null) => {
    const newError = validateSalesPeriod(salesStartAt, date, error);
    setError(newError), setSalesEndAt(date);
  };

  const handleSalesRelativeStartAt = (value: number) => {
    const newError = validateRelativeSalesPeriod(
      value,
      salesRelativeEndAt,
      error
    );
    setError(newError);
    setSalesRelativeStartAt(value);
  };

  const handleSalesRelativeEndAt = (value: number) => {
    const newError = validateRelativeSalesPeriod(
      salesRelativeStartAt,
      value,
      error
    );
    setError(newError);
    setSalesRelativeEndAt(value);
  };

  const handleInputTakeoutPrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const pattern = /^([0-9-]*)$/; // 半角数字とマイナス記号を許容する正規表現
    if (!pattern.test(newValue)) return;
    const newError = validateTakeoutPrice(newValue, error);
    setError(newError);
    setTakeout({ ...takeout, price: newValue });
  };

  const handleInputTakeoutTax = (newValue: React.ReactText) => {
    setTakeout({ ...takeout, tax_rate: Number(newValue) as TaxRate });
  };

  const handleChangeLimitOfPurchase = (value: number | null) => {
    const targetErrorKey = 'limitOfPurchase';
    if (value != null) {
      if (validMinNum.check(value, 0)) {
        error[targetErrorKey].isError = true;
        error[targetErrorKey].message =
          '購入上限数は1以上の値を入力してください';
        setError(error);
        return;
      }
      if (validMaxNum.check(value, 10)) {
        error[targetErrorKey].isError = true;
        error[targetErrorKey].message =
          '購入上限数は10以下の値を入力してください';
        setError(error);
        return;
      }
    }
    setLimitOfPurchase(value);
  };

  const handleInputTakeoutPosCode = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTakeout({ ...takeout, pos_code: e.target.value });
  };

  const handleInputTakeoutPosCodeCaseOptionGroupItem = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTakeout({ ...takeout, pos_code_case_option_group_item: e.target.value });
  };

  const handleInputDeliveryPrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const pattern = /^([0-9-]*)$/; // 半角数字とマイナス記号を許容する正規表現
    if (!pattern.test(newValue)) return;
    const newError = validateDeliveryPrice(newValue, error);
    setError(newError);
    setDelivery({ ...delivery, price: newValue });
  };

  const handleInputDeliveryTax = (newValue: React.ReactText) => {
    setDelivery({ ...delivery, tax_rate: Number(newValue) as TaxRate });
  };

  const handleInputDeliveryPosCode = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDelivery({ ...delivery, pos_code: e.target.value });
  };

  const handleInputDeliveryPosCodeCaseOptionGroupItem = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDelivery({
      ...delivery,
      pos_code_case_option_group_item: e.target.value,
    });
  };

  const handleInputEatInPrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const pattern = /^([0-9-]*)$/; // 半角数字とマイナス記号を許容する正規表現
    if (!pattern.test(newValue)) return;
    const newError = validateEatInPrice(newValue, error);
    setError(newError);
    setEatIn({ ...eatIn, price: newValue });
  };

  const handleInputEatInTax = (newValue: React.ReactText) => {
    setEatIn({ ...eatIn, tax_rate: Number(newValue) as TaxRate });
  };

  const handleInputEatInPosCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEatIn({ ...eatIn, pos_code: e.target.value });
  };

  const handleInputEatInPosCodeCaseOptionGroupItem = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEatIn({ ...eatIn, pos_code_case_option_group_item: e.target.value });
  };

  // 商品に関する要望欄の設定ハンドラ
  const handleChangeSpecialInstructionsEnabled = (enabled: boolean) => {
    setSpecialInstructionsEnabled(enabled);
    if (!enabled) {
      // OFFにした場合は値とエラーをリセット
      setSpecialInstructionsTitle('');
      setSpecialInstructionsPlaceholder('');
      setSpecialInstructionsRequired(false);
      const newError = validateSpecialInstructionsTitle('', false, error);
      setError(newError);
    }
  };

  const handleInputSpecialInstructionsTitle = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newTitle = e.target.value;
    const newError = validateSpecialInstructionsTitle(
      newTitle,
      specialInstructionsEnabled,
      error
    );
    setError(newError);
    setSpecialInstructionsTitle(newTitle);
  };

  const handleInputSpecialInstructionsPlaceholder = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPlaceholder = e.target.value;
    const newError = validateSpecialInstructionsPlaceholder(
      newPlaceholder,
      error
    );
    setError(newError);
    setSpecialInstructionsPlaceholder(newPlaceholder);
  };

  const handleChangeSpecialInstructionsRequired = (required: boolean) => {
    setSpecialInstructionsRequired(required);
  };

  // special_instructions_setting を取得する関数
  const getSpecialInstructionsSetting =
    (): SpecialInstructionsSetting | null => {
      if (!specialInstructionsEnabled || specialInstructionsTitle === '') {
        return null;
      }
      return {
        title: specialInstructionsTitle,
        placeholder:
          specialInstructionsPlaceholder !== ''
            ? specialInstructionsPlaceholder
            : null,
        required: specialInstructionsRequired,
      };
    };

  const handleOnDragEnd = (result: DropResult) => {
    const [reorderedItem] = selectedItems.splice(result.source.index, 1);
    selectedItems.splice(result.destination?.index ?? 0, 0, reorderedItem);
    setIsDragged((bool) => !bool);
    setSelectedItems(selectedItems);
  };

  const handleDeleteDragItem = (itemId: string) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemId);
    setSelectedItems(newSelectedItems);
  };

  const createS3Image = async (presignedUrl: string, file: File) => {
    await Axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  };

  const handleSearchItemNameInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newTitleJa = e.target.value;
    setSearchItemName(newTitleJa);
    setIsSearching(true);
    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      if (brandSelect === null) return;
      const optionGroups = await useMenuManagementSearchBrandOptionGroups({
        associate_brand_id: brandSelect.value,
        title: newTitleJa,
      });
      setSearchItems(optionGroups);
      setIsSearching(false);
    }, 700);
    setTimer(Number(newTimer));
  };

  const handleAddSelectItems = (item: SearchItem) => {
    setSearchItemName('');
    setSelectedItems([item, ...selectedItems]);
  };

  const getPriceAndPosCode = (
    obj: BrandItemTakeoutForm | BrandItemDeliveryForm | BrandItemEatInForm
  ) => {
    return {
      price: Number(obj.price),
      tax_rate: obj.tax_rate,
      pos_code: obj.pos_code !== '' ? obj.pos_code : null,
      pos_code_case_option_group_item:
        obj.pos_code_case_option_group_item !== ''
          ? obj.pos_code_case_option_group_item
          : null,
    };
  };

  const handleCreate = async () => {
    const brandValue = brandSelect?.value;
    if (brandValue === undefined) return;
    setIsLoading(true);
    if (image.file !== undefined) {
      try {
        await createS3Image(image.presigned_url, image.file);
      } catch (err) {
        const asserErr = err as AxiosError;
        setIsLoading(false);
        if (asserErr.response?.status === 403) {
          return alert(reRequestMessage(image.file.name));
        } else {
          return alert(validateMessage(image.file.name));
        }
      }
    }
    let newTakeout: BrandItemTakeout | null = null;
    if (orderMethods.includes('TAKE_OUT')) {
      newTakeout = getPriceAndPosCode(takeout);
    }
    let newDelivery: BrandItemDelivery | null = null;
    if (orderMethods.includes('DELIVERY')) {
      newDelivery = newDelivery = getPriceAndPosCode(delivery);
    }
    let newEatIn: BrandItemEatIn | null = null;
    if (orderMethods.includes('EAT_IN')) {
      newEatIn = getPriceAndPosCode(eatIn);
    }
    try {
      await useMenuManagementCreateBrandItem({
        associate_brand_id: Number(brandValue),
        title: {
          translation: {
            ja: titleJa,
          },
        },
        image_url: getRequestImageUrl(image.presigned_url, image.image_url),
        memo: memo !== '' ? memo : null,
        description: description !== '' ? description : null,
        sales_start_at:
          salesStartAt !== null
            ? DateDomainService.datetimeNoColonFormat(salesStartAt)
            : null,
        sales_end_at:
          salesEndAt !== null
            ? DateDomainService.datetimeNoColonFormat(salesEndAt)
            : null,
        takeout: newTakeout,
        delivery: newDelivery,
        eat_in: newEatIn,
        limit_of_purchase: limitOfPurchase,
        order_methods: orderMethods,
        brand_option_group_sort_objects: getSearchItems(selectedItems),
        can_serve_alone: canServeAlone === '1',
        is_auth_required: isAuthRequired === '1',
        sales_period_type: salesPeriodType,
        relative_sales_start_at: salesRelativeStartAt,
        relative_sales_end_at: salesRelativeEndAt,
        special_instructions_setting: getSpecialInstructionsSetting(),
      });
      setIsLoading(false);
      alert('商品を登録しました。');
      history.push(listUrl);
    } catch (err) {
      setIsLoading(false);
      if (err instanceof HttpError && err.status === 400) {
        return alert(err.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error: err });
    }
  };

  const handleUpdate = async (urlParamsId: string) => {
    setIsLoading(true);
    if (image.file !== undefined) {
      try {
        await createS3Image(image.presigned_url, image.file);
      } catch (err) {
        const asserErr = err as AxiosError;
        if (asserErr.response?.status === 403) {
          return alert(reRequestMessage(image.file.name));
        } else {
          return alert(validateMessage(image.file.name));
        }
      }
    }
    const brandValue = brandSelect?.value;
    try {
      await useMenuManagementUpdateBrandItem(urlParamsId, {
        associate_brand_id: Number(brandValue),
        title: {
          translation: {
            ja: titleJa,
          },
        },
        image_url: getRequestImageUrl(image.presigned_url, image.image_url),
        memo: memo !== '' ? memo : null,
        description: description !== '' ? description : null,
        sales_start_at:
          salesStartAt !== null
            ? DateDomainService.datetimeNoColonFormat(salesStartAt)
            : null,
        sales_end_at:
          salesEndAt !== null
            ? DateDomainService.datetimeNoColonFormat(salesEndAt)
            : null,
        sales_period_type: salesPeriodType,
        relative_sales_start_at: salesRelativeStartAt,
        relative_sales_end_at: salesRelativeEndAt,
        takeout: getPriceAndPosCode(takeout),
        delivery: getPriceAndPosCode(delivery),
        eat_in: getPriceAndPosCode(eatIn),
        limit_of_purchase: limitOfPurchase,
        order_methods: orderMethods,
        is_auth_required: isAuthRequired === '1',
        brand_option_group_sort_objects: getSearchItems(selectedItems),
        special_instructions_setting: getSpecialInstructionsSetting(),
      });
      setIsLoading(false);
      alert('商品を更新しました。');
      history.push(listUrl);
    } catch (err) {
      setIsLoading(false);
      if (err instanceof HttpError && err.status === 400) {
        return alert(err.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error: err });
    }
  };

  const handleDuplicate = async (urlParamsId: string) => {
    setIsLoading(true);
    if (image.file !== undefined) {
      try {
        await createS3Image(image.presigned_url, image.file);
      } catch (err) {
        const asserErr = err as AxiosError;
        if (asserErr.response?.status === 403) {
          return alert(reRequestMessage(image.file.name));
        } else {
          return alert(validateMessage(image.file.name));
        }
      }
    }
    try {
      await useMenuManagementDuplicateBrandItem(urlParamsId);
      setIsLoading(false);
      alert('商品を複製しました。');
      history.push(listUrl);
    } catch (err) {
      setIsLoading(false);
      if (err instanceof HttpError && err.status === 400) {
        return alert(err.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error: err });
    }
  };
  const initializeNewPageState = async () => {
    const associateBrands = await useAssociateBrandList();
    const associateBrandOptions = associateBrands.map((brand) => {
      return {
        label: brand.name,
        value: brand.id.toString(),
      };
    });
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    const initialAssociateBrand =
      associateBrand !== undefined ? associateBrand : associateBrandOptions[0];
    setBrandSelect(initialAssociateBrand);
    LocalStorageHandler.set('menuManagement', {
      associateBrand: initialAssociateBrand,
    });
    setAssociateBrandSelectOptions(associateBrandOptions);
  };
  const initializeEditPageState = async (urlParamsId: string) => {
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    if (associateBrand === undefined) return history.push(listUrl);
    const brandItem = await useMenuManagementBrandItemShow(urlParamsId);
    const newTitleJa = brandItem.title.translation.ja;
    const newMemo = brandItem.memo !== null ? brandItem.memo : '';
    const newDescription =
      brandItem.description !== null ? brandItem.description : '';
    const newSalesStartAt = brandItem.sales_start_at
      ? DateDomainService.parseNoColonFormat(brandItem.sales_start_at)
      : null;
    const newSalesEndAt = brandItem.sales_end_at
      ? DateDomainService.parseNoColonFormat(brandItem.sales_end_at)
      : null;
    const newImage = { image_url: '', file: undefined, presigned_url: '' };
    newImage.image_url =
      brandItem.image_url !== null ? brandItem.image_url : '';
    const newCanServeAlone = brandItem.can_serve_alone ? '1' : '0';
    const newIsAuthRequired = brandItem.is_auth_required ? '1' : '0';
    const newSelectedItems = brandItem.brand_option_group_sort_objects;
    const newSalesPeriodType = brandItem.sales_period_type;
    const newSalesRelativeStartAt = brandItem.relative_sales_start_at ?? 0;
    const newSalesRelativeEndAt = brandItem.relative_sales_end_at ?? 0;
    const {
      takeout,
      delivery,
      eat_in,
      limit_of_purchase: limitOfPurchase,
    } = brandItem;
    let newTakeout: BrandItemTakeoutForm = {
      price: '',
      tax_rate: 8,
      pos_code: '',
      pos_code_case_option_group_item: '',
    };
    let newDelivery: BrandItemDeliveryForm = {
      price: '',
      tax_rate: 8,
      pos_code: '',
      pos_code_case_option_group_item: '',
    };
    let newEatIn: BrandItemEatInForm = {
      price: '',
      tax_rate: 10,
      pos_code: '',
      pos_code_case_option_group_item: '',
    };
    if (takeout !== null) {
      const { price, tax_rate, pos_code, pos_code_case_option_group_item } =
        takeout;
      newTakeout = {
        price: price.toString(),
        tax_rate,
        pos_code: nullToEmptyString(pos_code),
        pos_code_case_option_group_item: nullToEmptyString(
          pos_code_case_option_group_item
        ),
      };
    }
    const orderMethods = brandItem.order_methods;
    if (delivery !== null) {
      const { price, tax_rate, pos_code, pos_code_case_option_group_item } =
        delivery;
      newDelivery = {
        price: price.toString(),
        tax_rate,
        pos_code: pos_code !== null ? pos_code : '',
        pos_code_case_option_group_item:
          pos_code_case_option_group_item !== null
            ? pos_code_case_option_group_item
            : '',
      };
    }
    if (eat_in !== null) {
      const { price, tax_rate, pos_code, pos_code_case_option_group_item } =
        eat_in;
      newEatIn = {
        price: price.toString(),
        tax_rate,
        pos_code: pos_code !== null ? pos_code : '',
        pos_code_case_option_group_item:
          pos_code_case_option_group_item !== null
            ? pos_code_case_option_group_item
            : '',
      };
    }
    setBrandSelect(associateBrand);
    setTitleJa(newTitleJa);
    setMemo(newMemo);
    setDescription(newDescription);
    setSalesStartAt(newSalesStartAt);
    setSalesEndAt(newSalesEndAt);
    setImage(newImage);
    setCanServeAlone(newCanServeAlone);
    setIsAuthRequired(newIsAuthRequired);
    setSelectedItems(newSelectedItems);
    setLimitOfPurchase(limitOfPurchase);
    setTakeout(newTakeout);
    setDelivery(newDelivery);
    setEatIn(newEatIn);
    setOrderMethods(orderMethods);
    setSalesPeriodType(newSalesPeriodType);
    setSalesRelativeStartAt(newSalesRelativeStartAt);
    setSalesRelativeEndAt(newSalesRelativeEndAt);
    // 商品に関する要望欄の設定を読み込む
    const setting = brandItem.special_instructions_setting;
    if (setting != null) {
      setSpecialInstructionsEnabled(true);
      setSpecialInstructionsTitle(setting.title);
      setSpecialInstructionsPlaceholder(setting.placeholder ?? '');
      setSpecialInstructionsRequired(setting.required);
    } else {
      setSpecialInstructionsEnabled(false);
      setSpecialInstructionsTitle('');
      setSpecialInstructionsPlaceholder('');
      setSpecialInstructionsRequired(false);
    }
    setInitialStringifyFormState(
      JSON.stringify([
        newTitleJa,
        newMemo,
        limitOfPurchase,
        newDescription,
        newSalesStartAt,
        newSalesEndAt,
        newImage,
        newSelectedItems,
        newTakeout,
        newDelivery,
        newEatIn,
        orderMethods,
        newIsAuthRequired,
        newSalesPeriodType,
        newSalesRelativeStartAt,
        newSalesRelativeEndAt,
      ])
    ); // WARNING 文字列比較なので順番を変更するときは注意
  };
  const handleDelete = async (urlParamsId: string) => {
    try {
      /* バックエンドでmongoのトランザクションを実装しているがローカルだと500エラになる。 */
      setIsLoading(true);
      await useMenuManagementDeleteBrandItem(urlParamsId);
      setIsLoading(false);
      alert('商品を削除しました。');
      history.push(listUrl);
    } catch (error) {
      setIsLoading(false);
      if (error instanceof HttpError && error.status === 400) {
        return alert(error.message);
      }
      // @ts-ignore TS2322
      errorHandler({ error });
    }
  };
  const handleListAction = (
    urlParamsId: string,
    actiontype: ManagementItemlistActionType
  ) => {
    switch (actiontype) {
      case MANAGEMENTITEMLISTACTION_TYPE.NOSELECT:
        return;
      case MANAGEMENTITEMLISTACTION_TYPE.DUPLICATE:
        return handleDuplicate(urlParamsId);
      case MANAGEMENTITEMLISTACTION_TYPE.DELETE:
        return handleDelete(urlParamsId);
    }
  };
  return {
    associateBrandSelectInput,
    titleJa,
    image,
    memo,
    takeout,
    delivery,
    eatIn,
    description,
    salesStartAt,
    salesEndAt,
    error,
    searchItems,
    selectedItems,
    limitOfPurchase,
    handleChangeLimitOfPurchase,
    searchItemName,
    isSearching,
    targetsHeight,
    canServeAlone,
    isAuthRequired,
    orderMethods,
    initialStringifyFormState,
    isLoading,
    setIsLoading,
    handleUpdate,
    handleDuplicate,
    handleCreate,
    handleInputName,
    handleChangeImageUrl,
    handleClearImage,
    handleChangeCanServeAlone,
    handleChangeOrderMethod,
    handleInputMemo,
    handleInputDescription,
    handleInputSalesStartAt,
    handleInputSalesEndAt,
    handleInputTakeoutPrice,
    handleInputTakeoutPosCode,
    handleInputTakeoutPosCodeCaseOptionGroupItem,
    handleInputTakeoutTax,
    handleInputDeliveryPrice,
    handleInputDeliveryPosCode,
    handleInputDeliveryPosCodeCaseOptionGroupItem,
    handleInputDeliveryTax,
    handleInputEatInPrice,
    handleInputEatInPosCode,
    handleInputEatInPosCodeCaseOptionGroupItem,
    handleInputEatInTax,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    initializeNewPageState,
    initializeEditPageState,
    handleChangeIsAuthRequired,
    salesPeriodType,
    handleSalesPeriodType,
    handleSalesRelativeStartAt,
    handleSalesRelativeEndAt,
    salesRelativeStartAt,
    salesRelativeEndAt,
    handleListAction,
    // 商品に関する要望欄の設定
    specialInstructionsEnabled,
    specialInstructionsTitle,
    specialInstructionsPlaceholder,
    specialInstructionsRequired,
    handleChangeSpecialInstructionsEnabled,
    handleInputSpecialInstructionsTitle,
    handleInputSpecialInstructionsPlaceholder,
    handleChangeSpecialInstructionsRequired,
  };
};
